/**
 * Crime Ops – Tab "GEWÄCHSHAUS"
 *
 * Zeigt alle aktiven Grow Rooms über alle eigenen Verstecke hinweg.
 * Anbau, Gießen, Ernte — alle Aktionen direkt hier.
 * Polling alle 30 Sekunden. Kein WebSocket nötig.
 */

import { api, ApiError } from '../../services/api.js';
import { renderItemBadges } from '../../utils/item-badges.js';
import { renderResponsiveTable, injectResponsiveTableStyles } from '../../utils/responsive-table.js';
import { formatHideoutName } from '../../utils/hideout-format.js';
import { filterHideoutsByExpansion, renderHideoutSelectorTabs } from '../../utils/hideout-selector.js';
import { tryOfferEmergencyGarage } from '../EmergencyGarageOfferModal.js';
import { renderWhenIdle, cancelPendingRender, isUserInteracting } from '../../utils/background-render.js';
import { calcNoticeDisplayMs } from '../../utils/notice-duration.js';

const GW_RENDER_KEY = 'gewaechshaus';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface GardenPlant {
  id:                     string;
  strainKey:              string;
  phase:                  'GERMINATION' | 'GROWING' | 'HARVEST_READY';
  potIndex:               number;
  germinationStartedAt:   string | null;
  germinationCompletesAt: string | null;
  growthStartedAt:        string | null;
  growthCompletesAt:      string | null;
  originalGrowthCompletesAt: string | null;
  wateringsDone:          number;
  lastWateredAt:          string | null;
  outputBonus:            number;
  isHarvested:            boolean;
}

interface PropertySlot {
  id:             string;
  slotType:       string;
  slotIndex:      number;
  isUnlocked:     boolean;
  tentItem:       string | null;
  lampItem:       string | null;
  potItem:        string | null;
  filterItem:     string | null;
  lampItem2:      string | null;
  potItem2:       string | null;
  lampItem3:      string | null;
  potItem3:       string | null;
  lampItem4:      string | null;
  potItem4:       string | null;
  irrigationItem: string | null;
  activePlants:   GardenPlant[];
}

interface InventoryItem {
  id:        string;
  itemType:  string;
  itemKey:   string;
  quantity:  number;
  expiresAt: string | null;
}

interface Hideout {
  id:                          string;
  subType:                     string;
  customName:                  string | null;
  nickname:                    string | null;
  expansionType:               'GROW_ROOM' | 'LABOR' | null;
  expansionLevel:              number;
  expansionUpgradeCompletesAt: string | null;
  propertySlots:               PropertySlot[];
  inventory:                   InventoryItem[];
  /** NT-ORG-06 — gesetzt, wenn das Versteck ein Org-Gebäude ist (gemeinsame Produktion). */
  orgBuilding?:                { orgId: string } | null;
}

function isOrgBuilding(h: Hideout): boolean {
  return h.orgBuilding !== undefined && h.orgBuilding !== null;
}

// ─── Trocknungs-Typen ─────────────────────────────────────────────────────────

interface DryingSlotView {
  slotIndex:    number;
  occupied:     boolean;
  jobId:        string | null;
  inputItemKey: string | null;
  quantity:     number | null;
  startedAt:    string | null;
  completesAt:  string | null;
  isDone:       boolean;
}

interface DryingRackView {
  rackIndex: number;
  slots:     DryingSlotView[];
}

interface DryingStatusView {
  installedRacks:  number;
  maxRacks:        number;
  rackInInventory: number;
  racks:           DryingRackView[];
}

// ─── Lokale Strain-Konfiguration (spiegelt Backend STRAIN_CONFIG) ─────────────

interface FrontendStrainDef {
  key:                string;
  label:              string;
  germinationMinutes: number;
  growthMinutes:      number;
  yieldMin:           number;
  yieldMax:           number;
  basePriceCents:     number;
  requiredTalentId:   string | null;
}

const STRAINS: readonly FrontendStrainDef[] = [
  { key: 'kraeuter_mix',     label: 'Kräuter-Mix',      germinationMinutes:  60, growthMinutes:  720, yieldMin:  8, yieldMax: 12, basePriceCents:  1_500, requiredTalentId: null },
  { key: 'stadtgras',        label: 'Stadtgras',         germinationMinutes:  60, growthMinutes: 1_080, yieldMin:  3, yieldMax:  5, basePriceCents:  5_500, requiredTalentId: null },
  { key: 'nordlicht',        label: 'Nordlicht',         germinationMinutes: 120, growthMinutes: 1_440, yieldMin:  4, yieldMax:  7, basePriceCents:  6_500, requiredTalentId: null },
  { key: 'balkonkoenig',     label: 'Balkonkönig',       germinationMinutes: 180, growthMinutes: 1_800, yieldMin:  5, yieldMax:  9, basePriceCents:  8_000, requiredTalentId: 'gruener_daumen_sorten_kenner' },
  { key: 'gruener_riese',    label: 'Grüner Riese',      germinationMinutes: 240, growthMinutes: 2_880, yieldMin:  8, yieldMax: 14, basePriceCents:  7_500, requiredTalentId: 'gruener_daumen_sorten_kenner' },
  { key: 'purpurkrone',      label: 'Purpurkrone',       germinationMinutes: 300, growthMinutes: 3_600, yieldMin:  6, yieldMax: 10, basePriceCents: 13_000, requiredTalentId: 'gruener_daumen_hybridzucht' },
  { key: 'weisser_nebel',    label: 'Weißer Nebel',      germinationMinutes: 360, growthMinutes: 4_320, yieldMin:  5, yieldMax:  8, basePriceCents: 16_000, requiredTalentId: 'gruener_daumen_hybridzucht' },
  { key: 'kristallkind',     label: 'Kristallkind',      germinationMinutes: 360, growthMinutes: 5_040, yieldMin:  4, yieldMax:  7, basePriceCents: 21_000, requiredTalentId: 'gruener_daumen_meistergaertner' },
  { key: 'schwarzer_diamant',label: 'Schwarzer Diamant', germinationMinutes: 480, growthMinutes: 5_760, yieldMin:  3, yieldMax:  5, basePriceCents: 35_000, requiredTalentId: 'gruener_daumen_meistergaertner' },
  // Exotische Sorten (Darknet-exklusiv, NT-STRAINS-EXOTIC-01) — kein Talent-Gate, Saatgut
  // kommt über den künftigen Darknet-Shop (NT-DARKNET-01), noch nicht erwerbbar.
  { key: 'schwarzwald_diesel', label: 'Schwarzwald-Diesel', germinationMinutes:  45, growthMinutes:   360, yieldMin:  6, yieldMax: 10, basePriceCents:  9_000, requiredTalentId: null },
  { key: 'silberfell',         label: 'Silberfell',         germinationMinutes: 480, growthMinutes: 2_880, yieldMin:  2, yieldMax:  4, basePriceCents: 45_000, requiredTalentId: null },
  // Tabak-Sorten (Talent: gruener_daumen_tabakverarbeitung)
  { key: 'landtabak',        label: 'Landtabak',         germinationMinutes:  60, growthMinutes:   720, yieldMin:  6, yieldMax: 10, basePriceCents:  2_000, requiredTalentId: 'gruener_daumen_tabakverarbeitung' },
  { key: 'goldblatt',        label: 'Goldblatt',         germinationMinutes:  90, growthMinutes: 1_080, yieldMin:  4, yieldMax:  7, basePriceCents:  4_500, requiredTalentId: 'gruener_daumen_tabakverarbeitung' },
  { key: 'habano_klon',      label: 'Habano-Klon',       germinationMinutes: 120, growthMinutes: 1_440, yieldMin:  3, yieldMax:  6, basePriceCents:  7_000, requiredTalentId: 'gruener_daumen_tabakverarbeitung' },
  { key: 'black_leaf',       label: 'Black Leaf',        germinationMinutes: 480, growthMinutes: 5_760, yieldMin:  3, yieldMax:  5, basePriceCents: 28_000, requiredTalentId: 'gruener_daumen_tabakverarbeitung' },
] as const;

const STRAIN_MAP = new Map<string, FrontendStrainDef>(STRAINS.map((s) => [s.key, s]));

// Tabak-Strain-Label-Map (spiegelt TABAK_STRAINS aus Backend)
const TOBACCO_LABELS: Record<string, string> = {
  landtabak:   'Landtabak',
  goldblatt:   'Goldblatt',
  habano_klon: 'Habano-Klon',
  black_leaf:  'Black Leaf',
};

const TOBACCO_KEYS = new Set(Object.keys(TOBACCO_LABELS));

const TALENT_LABELS: Record<string, string> = {
  'gruener_daumen_sorten_kenner':     'Sorten-Kenner',
  'gruener_daumen_hybridzucht':       'Hybridzucht',
  'gruener_daumen_meistergaertner':   'Meistergärtner',
  'gruener_daumen_tabakverarbeitung': 'Tabakverarbeitung',
};

const QUALITY_LABELS: Record<string, string> = {
  '1': 'AUSSCHUSS', '2': 'HINTERHOF-WARE', '3': 'MARKTWARE', '4': 'FEINWARE', '5': 'PREMIUM-ERNTE',
};

// Preismultiplikatoren für Hanf nach Qualitätsstufe (Q3 = Basispreis)
const HEMP_QUALITY_PRICE_MODIFIERS: Readonly<Record<number, number>> = {
  1: 0.50, 2: 0.75, 3: 1.00, 4: 1.35, 5: 1.80,
};


/**
 * Liest eine Pflicht-Capture-Gruppe aus einem Regex-Match. Nach `if (!m) return`/`if (m)` sind
 * laut Definition der jeweiligen Regex alle Gruppen dieses Patterns garantiert vorhanden —
 * TypeScript trackt das bei RegExpExecArray-Indexzugriffen nicht (noUncheckedIndexedAccess).
 */
function requireGroup(m: RegExpExecArray, index: number): string {
  const value = m[index];
  if (value === undefined) throw new Error(`Regex-Gruppe ${String(index)} fehlt unerwartet (Pattern-Bug).`);
  return value;
}

// Parst einen Qualitäts-Hanf-Key (z.B. "nordlicht_q3") → { strainKey, tier }
function parseHempHarvestKey(key: string): { strainKey: string; tier: number } | null {
  const m = /^([a-z][a-z0-9_]*)_q([1-5])$/.exec(key);
  if (!m) return null;
  const strainKey = requireGroup(m, 1);
  if (!STRAIN_MAP.has(strainKey) || TOBACCO_KEYS.has(strainKey)) return null;
  return { strainKey, tier: parseInt(requireGroup(m, 2), 10) };
}

function parseTobaccoWetKey(key: string): string {
  const m = /^feuchter_tabak_([a-z0-9_]+)_q([1-5])$/.exec(key);
  if (m) {
    const strainKey = requireGroup(m, 1);
    const tierKey   = requireGroup(m, 2);
    const strain = TOBACCO_LABELS[strainKey] ?? strainKey;
    const q      = QUALITY_LABELS[tierKey] ?? `Q${tierKey}`;
    return `${strain} (feucht) [${q}]`;
  }
  const d = /^deckblatt_roh_q([1-5])$/.exec(key);
  if (d) {
    const tierKey = requireGroup(d, 1);
    return `Deckblatt (roh) [${QUALITY_LABELS[tierKey] ?? `Q${tierKey}`}]`;
  }
  return key;
}

function parseTobaccoDriedKey(key: string): string {
  const m = /^getrockneter_tabak_([a-z0-9_]+)_q([1-5])$/.exec(key);
  if (m) {
    const strainKey = requireGroup(m, 1);
    const tierKey   = requireGroup(m, 2);
    const strain = TOBACCO_LABELS[strainKey] ?? strainKey;
    const q      = QUALITY_LABELS[tierKey] ?? `Q${tierKey}`;
    return `${strain} (getrocknet) [${q}]`;
  }
  const d = /^deckblatt_getrocknet_q([1-5])$/.exec(key);
  if (d) {
    const tierKey = requireGroup(d, 1);
    return `Deckblatt (getrocknet) [${QUALITY_LABELS[tierKey] ?? `Q${tierKey}`}]`;
  }
  return key;
}

function parseTobaccoDriedSorte(key: string): string {
  const m = /^getrockneter_tabak_([a-z0-9_]+)_q[1-5]$/.exec(key);
  if (m) {
    const strainKey = requireGroup(m, 1);
    return `${TOBACCO_LABELS[strainKey] ?? strainKey} (getrocknet)`;
  }
  if (/^deckblatt_getrocknet_q[1-5]$/.test(key)) return 'Deckblatt (getrocknet)';
  return key;
}

// ─── Watering-Konfiguration (gespiegelt aus Backend grow.service.ts getWaterConfig,
//     NT-BUGFIXES-01 BUG 5 — Pro-Sorte-Ableitung statt fixer globaler Zeit-Brackets) ──

interface WateringConfig {
  maxWaters: number;
  intervalMs: number;
}

const WATER_TARGET_COUNT = 8;
const WATER_MIN_INTERVAL_MS = 90 * 60 * 1_000;
const WATER_MAX_INTERVAL_MS = 3 * 60 * 60 * 1_000;
const WATER_MIN_COUNT = 3;

function getWaterConfig(totalGrowthMs: number): WateringConfig {
  const rawIntervalMs = totalGrowthMs / WATER_TARGET_COUNT;
  const intervalMs = Math.min(WATER_MAX_INTERVAL_MS, Math.max(WATER_MIN_INTERVAL_MS, rawIntervalMs));
  const maxWaters = Math.max(WATER_MIN_COUNT, Math.floor(totalGrowthMs / intervalMs) - 1);
  return { maxWaters, intervalMs };
}

// ─── State ────────────────────────────────────────────────────────────────────

let growHideouts:      Hideout[] = [];     // nur Verstecke mit aktivem Grow Room
let selectedIdx    = 0;
let selectedStrain:    Record<string, string> = {};  // `${slotId}:${potIndex}` → strainKey
let isLoading   = true;
let loadError:         string | null = null;
let refreshTimer:      ReturnType<typeof setInterval> | null = null;
let countdownTimer:    ReturnType<typeof setInterval> | null = null;
let reloadPromise:     Promise<void> | null = null;
const cooldownOverrideUntilMs = new Map<string, number>();

// Trocknung — pro propertyId gecacht
const dryingCache       = new Map<string, DryingStatusView>();
// Trocknungs-Start-Formular: propertyId → rackIndex → slotIndex → { itemKey, qty }
let dryingForm: Record<string, Record<number, Record<number, { itemKey: string; qty: number }>>> = {};

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtSNox(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' S-₦';
}

function fmtNox(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₦';
}

function fmtDuration(minutes: number): string {
  if (minutes < 60) return `${String(minutes)}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${String(h)}h ${String(m)}min` : `${String(h)}h`;
}

function timeLeft(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'FERTIG';
  if (ms < 5 * 60_000) {
    const totalSec = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${String(h)}h ${String(m)}min` : `${String(m)}min`;
}

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1_000));
  const hours = Math.floor(total / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const seconds = total % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function getTotalGrowthMs(plant: GardenPlant, strain: FrontendStrainDef | undefined): number {
  if (plant.originalGrowthCompletesAt && plant.growthStartedAt) {
    const ms = new Date(plant.originalGrowthCompletesAt).getTime() - new Date(plant.growthStartedAt).getTime();
    if (ms > 0) return ms;
  }

  return (strain?.growthMinutes ?? 720) * 60 * 1_000;
}

function getCurrentCooldownMs(plant: GardenPlant, config: WateringConfig): number {
  const overrideUntil = cooldownOverrideUntilMs.get(plant.id) ?? 0;
  const overrideRemaining = Math.max(0, overrideUntil - Date.now());
  if (overrideRemaining > 0) return overrideRemaining;

  if (!plant.lastWateredAt) return 0;
  const elapsedMs = Date.now() - new Date(plant.lastWateredAt).getTime();
  return Math.max(0, config.intervalMs - elapsedMs);
}

function hasActiveWaterCooldowns(): boolean {
  for (const hideout of growHideouts) {
    for (const slot of hideout.propertySlots) {
      for (const plant of slot.activePlants) {
        if (plant.phase !== 'GROWING') continue;
        const strain = STRAIN_MAP.get(plant.strainKey);
        const totalGrowthMs = getTotalGrowthMs(plant, strain);
        const config = getWaterConfig(totalGrowthMs);
        if (plant.wateringsDone >= config.maxWaters) continue;
        if (getCurrentCooldownMs(plant, config) > 0) {
          return true;
        }
      }
    }
  }

  return false;
}

function hasExpiredPhase(): boolean {
  const now = Date.now();
  for (const hideout of growHideouts) {
    for (const slot of hideout.propertySlots) {
      for (const plant of slot.activePlants) {
        if (plant.phase === 'GERMINATION' && plant.germinationCompletesAt) {
          if (new Date(plant.germinationCompletesAt).getTime() <= now) return true;
        }
        if (plant.phase === 'GROWING' && plant.growthCompletesAt) {
          if (new Date(plant.growthCompletesAt).getTime() <= now) return true;
        }
      }
    }
  }
  return false;
}

function progressPct(startedAt: string | null, completesAt: string | null): number {
  if (!startedAt || !completesAt) return 0;
  const total   = new Date(completesAt).getTime() - new Date(startedAt).getTime();
  const elapsed = Date.now() - new Date(startedAt).getTime();
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
}

function progressColor(pct: number): string {
  if (pct >= 90) return 'var(--accent-gold)';
  return 'var(--accent-green)';
}

function hideoutLabel(h: Hideout): string {
  const name = formatHideoutName(h).toUpperCase();
  return isOrgBuilding(h) ? `ORG · ${name}` : name;
}

function getAvailableSeedKeys(hideout: Hideout): Set<string> {
  return new Set(
    hideout.inventory
      .filter((item) => item.itemType === 'GARDEN' && item.quantity > 0 && item.itemKey.startsWith('samen_'))
      .map((item) => item.itemKey.replace(/^samen_/, '')),
  );
}

function isUpgradeRunning(h: Hideout): boolean {
  return h.expansionUpgradeCompletesAt !== null &&
    new Date(h.expansionUpgradeCompletesAt).getTime() > Date.now();
}

// NT-HOTFIX-01 (H5): Listener werden genau EINMAL pro Root-Element angehängt (Event-Delegation).
// Vorher hängte jeder rerender() — der Countdown rendert bei laufender Gieß-Sperre jede Sekunde —
// neue click-/change-Listener an dasselbe Root-Element; der change-Handler rendert selbst neu und
// verdoppelte die Handler dadurch bei jedem Event → Browser-Freeze.
let listenersRoot: HTMLElement | null = null;

// NT-HOTFIX-01 (H5): In-flight-Sperre pro Aktion+Ziel. Solange ein Request läuft, bleibt der
// Button gesperrt — auch über Re-Renders hinweg (die das Markup sonst wieder aktivieren würden).
// Keine Request-Warteschlange: weitere Klicks werden verworfen.
const inFlight = new Set<string>();

function inFlightSelector(key: string): string {
  const [action, ...rest] = key.split(':');
  const target = CSS.escape(rest.join(':'));
  switch (action) {
    case 'plant':   return `[data-gw-plant="${target}"]`;
    case 'water':   return `[data-gw-water="${target}"]`;
    case 'harvest': return `[data-gw-harvest="${target}"]`;
    case 'apply':   return `[data-gw-apply="${target}"]`;
    default:        return '';
  }
}

function applyInFlightState(root: HTMLElement): void {
  for (const key of inFlight) {
    const selector = inFlightSelector(key);
    if (!selector) continue;
    root.querySelectorAll<HTMLButtonElement>(selector).forEach((btn) => { btn.disabled = true; });
  }
}

/** Führt eine Aktion nur aus, wenn für diesen Schlüssel kein Request läuft. */
function runExclusive(key: string, action: () => Promise<void>): void {
  if (inFlight.has(key)) return;
  inFlight.add(key);
  void action().finally(() => {
    inFlight.delete(key);
    rerender();
  });
}

function rerender(): void {
  const root = document.getElementById('gw-tab-root');
  if (!root) return;
  root.innerHTML = renderContent();
  applyInFlightState(root);
  attachListeners();
}

// ─── Per-Topf-Karten ──────────────────────────────────────────────────────────

function potKey(slotId: string, potIndex: number): string {
  return `${slotId}:${String(potIndex)}`;
}

/** Fall A: Topf nicht konfiguriert */
function renderPotA(slot: PropertySlot, potIndex: number): string {
  return `<div class="gw-slot gw-slot--unconfigured">
    <p class="gw-slot-title">ZELT ${String(slot.slotIndex + 1)} / TOPF ${String(potIndex)} — NICHT KONFIGURIERT</p>
    <p class="gw-hint">Lampe und Topf ${String(potIndex)} unter IMMOBILIEN einrichten.</p>
  </div>`;
}

/** Fall B: Konfiguriert, keine Pflanze → Sorte wählen + ANBAUEN */
function renderPotB(slot: PropertySlot, potIndex: number, hideout: Hideout): string {
  const key     = potKey(slot.id, potIndex);
  const availableSeedKeys = getAvailableSeedKeys(hideout);
  const availableSeeds = STRAINS.filter((s) => availableSeedKeys.has(s.key));
  const current = availableSeedKeys.has(selectedStrain[key] ?? '') ? (selectedStrain[key] ?? '') : '';
  const strainDef = STRAIN_MAP.get(current);

  const optsHtml = availableSeeds.length > 0
    ? [`<option value="">— Sorte wählen —</option>`, ...availableSeeds.map((s) => {
        const talentNote = s.requiredTalentId ? ' *' : '';
        return `<option value="${esc(s.key)}"${current === s.key ? ' selected' : ''}>${esc(s.label)}${talentNote}</option>`;
      })].join('')
    : '<option value="" selected disabled>KEINE SAMEN IM LAGER</option>';

  let infoHtml = '';
  if (strainDef) {
    const isTobacco   = TOBACCO_KEYS.has(strainDef.key);
    const priceStr    = isTobacco ? fmtNox(strainDef.basePriceCents) : fmtSNox(strainDef.basePriceCents);
    const talentLabel = strainDef.requiredTalentId
      ? (TALENT_LABELS[strainDef.requiredTalentId] ?? strainDef.requiredTalentId)
      : null;
    infoHtml = `
      <div class="gw-strain-info">
        <span class="gw-si-row"><span class="gw-si-key">WACHSTUM</span><span class="gw-si-val">${esc(fmtDuration(strainDef.germinationMinutes))} Keimung + ${esc(fmtDuration(strainDef.growthMinutes))} Wachstum</span></span>
        <span class="gw-si-row"><span class="gw-si-key">ERTRAG</span><span class="gw-si-val">${String(strainDef.yieldMin)}–${String(strainDef.yieldMax)} Stk.</span></span>
        <span class="gw-si-row"><span class="gw-si-key">BASISPREIS</span><span class="gw-si-val" style="color:var(--accent-gold);">${esc(priceStr)}/Stk.</span></span>
        ${talentLabel ? `<span class="gw-si-row"><span class="gw-si-key">TALENT</span><span class="gw-si-val" style="color:var(--accent-purple);">${esc(talentLabel)}</span></span>` : ''}
      </div>`;
  }

  if (availableSeeds.length === 0) {
    infoHtml = '<p class="gw-hint">Keine Samen im Lager.</p>';
  }

  const plantDataKey = `${esc(slot.id)}:${String(potIndex)}`;
  return `<div class="gw-slot gw-slot--ready">
    <p class="gw-slot-title">ZELT ${String(slot.slotIndex + 1)} / TOPF ${String(potIndex)} — BEREIT</p>
    <div class="gw-slot-row">
      <select class="gw-select" data-gw-strain-select="${plantDataKey}">
        ${optsHtml}
      </select>
      <button class="gw-btn gw-btn--plant" data-gw-plant="${plantDataKey}"${!current ? ' disabled' : ''}>ANBAUEN</button>
    </div>
    ${infoHtml}
    <p class="gw-note">* Talent erforderlich</p>
  </div>`;
}

/** Fall C: KEIMUNG */
function renderPotC(slot: PropertySlot, potIndex: number, plant: GardenPlant): string {
  const germinationDone = plant.germinationCompletesAt
    ? new Date(plant.germinationCompletesAt).getTime() <= Date.now()
    : false;

  if (germinationDone) {
    void reload();
    return `<div class="gw-slot gw-slot--growing">
      <p class="gw-slot-title">ZELT ${String(slot.slotIndex + 1)} / TOPF ${String(potIndex)} — ÜBERGANG</p>
      <p class="gw-hint">Aktualisiere...</p>
    </div>`;
  }

  const pct   = progressPct(plant.germinationStartedAt, plant.germinationCompletesAt);
  const color = progressColor(pct);
  const left  = plant.germinationCompletesAt ? timeLeft(plant.germinationCompletesAt) : '?';
  const strain = STRAIN_MAP.get(plant.strainKey);

  return `<div class="gw-slot gw-slot--growing">
    <p class="gw-slot-title">ZELT ${String(slot.slotIndex + 1)} / TOPF ${String(potIndex)} — ${esc(strain?.label ?? plant.strainKey)} — KEIMT</p>
    <div class="gw-prog-bar"><div class="gw-prog-fill" style="width:${String(pct)}%;background:${color};"></div></div>
    <div class="gw-slot-meta"><span style="color:${color};">KEIMT — noch ${esc(left)}</span></div>
    <button class="gw-btn" disabled>GIESSEN (erst im Wachstum)</button>
  </div>`;
}

/** Fall D: WACHSTUM */
function renderPotD(slot: PropertySlot, potIndex: number, plant: GardenPlant): string {
  const growthDone = plant.growthCompletesAt
    ? new Date(plant.growthCompletesAt).getTime() <= Date.now()
    : false;

  if (growthDone) {
    void reload();
    return `<div class="gw-slot gw-slot--growing">
      <p class="gw-slot-title">ZELT ${String(slot.slotIndex + 1)} / TOPF ${String(potIndex)} — ÜBERGANG</p>
      <p class="gw-hint">Aktualisiere...</p>
    </div>`;
  }

  const pct    = progressPct(plant.growthStartedAt, plant.growthCompletesAt);
  const color  = progressColor(pct);
  const left   = plant.growthCompletesAt ? timeLeft(plant.growthCompletesAt) : '?';
  const strain = STRAIN_MAP.get(plant.strainKey);

  const totalGrowthMs = getTotalGrowthMs(plant, strain);
  const watCfg        = getWaterConfig(totalGrowthMs);
  const done          = plant.wateringsDone;
  const maxW          = watCfg.maxWaters;
  const maxReached    = done >= maxW;
  // Kein Gießen in den letzten intervalMs vor der Ernte — letzter Guss braucht Zeit zum Wirken
  const tooLateToWater = plant.growthCompletesAt
    ? (new Date(plant.growthCompletesAt).getTime() - Date.now()) < watCfg.intervalMs
    : false;
  const cooldownMs    = (maxReached || tooLateToWater) ? 0 : getCurrentCooldownMs(plant, watCfg);
  const isOnCooldown  = cooldownMs > 0;

  const waterBtn = maxReached
    ? `<button class="gw-btn" disabled>MAX. ERREICHT</button>`
    : tooLateToWater
      ? `<button class="gw-btn" disabled>ZU SPÄT ZUM GIESSEN</button>`
      : isOnCooldown
        ? `<button class="gw-btn" disabled>${formatCountdown(cooldownMs)}</button>`
        : `<button class="gw-btn gw-btn--water" data-gw-water="${esc(plant.id)}">GIESSEN</button>`;

  const waterStatus = isOnCooldown
    ? `GÜSSE: ${String(done)} / ${String(maxW)}   |   NÄCHSTER GUSS IN: <span class="gw-water-status-time">${formatCountdown(cooldownMs)}</span>`
    : `GÜSSE: ${String(done)} / ${String(maxW)}   |   <span class="gw-water-status-ready">JETZT GIESSBAR</span>`;

  // Verbrauchsmittel: nur sichtbar wenn Sorte im Inventar der Verstecke verfügbar
  const hideout = growHideouts[selectedIdx];
  const invKeys = hideout ? new Set(hideout.inventory.map((i) => i.itemKey)) : new Set<string>();
  const hasNaehrl  = invKeys.has('naehrloesung');
  const hasCo2Pad  = invKeys.has('co2_pad');

  const applyBtns = (hasNaehrl || hasCo2Pad) ? `
    <div class="gw-apply-row">
      ${hasNaehrl  ? `<button class="gw-btn gw-btn--apply" data-gw-apply="${esc(plant.id)}" data-gw-apply-item="naehrloesung">NÄHRLÖSUNG <span class="gw-water-save">+10% Ertrag</span></button>` : ''}
      ${hasCo2Pad  ? `<button class="gw-btn gw-btn--apply" data-gw-apply="${esc(plant.id)}" data-gw-apply-item="co2_pad">CO2-PAD <span class="gw-water-save">−10% Zeit</span></button>` : ''}
    </div>` : '';

  return `<div class="gw-slot gw-slot--growing">
    <p class="gw-slot-title">ZELT ${String(slot.slotIndex + 1)} / TOPF ${String(potIndex)} — ${esc(strain?.label ?? plant.strainKey)} — WÄCHST</p>
    <div class="gw-prog-bar"><div class="gw-prog-fill" style="width:${String(pct)}%;background:${color};"></div></div>
    <div class="gw-slot-meta"><span style="color:${color};">WÄCHST — noch ${esc(left)}</span></div>
    ${waterBtn}
    <p class="gw-water-status">${waterStatus}</p>
    ${applyBtns}
  </div>`;
}

/** Fall E: ERNTE BEREIT */
function renderPotE(slot: PropertySlot, potIndex: number, plant: GardenPlant): string {
  const strain = STRAIN_MAP.get(plant.strainKey);
  return `<div class="gw-slot gw-slot--harvest">
    <p class="gw-slot-title" style="color:var(--accent-green);">ZELT ${String(slot.slotIndex + 1)} / TOPF ${String(potIndex)} — ${esc(strain?.label ?? plant.strainKey)} — ERNTE BEREIT</p>
    <p class="gw-hint-green">Ertrag: ${String(strain?.yieldMin ?? '?')}–${String(strain?.yieldMax ?? '?')} Stk.</p>
    <button class="gw-btn gw-btn--harvest" data-gw-harvest="${esc(plant.id)}">ERNTEN</button>
  </div>`;
}

function renderPot(slot: PropertySlot, potIndex: 1 | 2 | 3 | 4, hideout: Hideout): string {
  const potItem  = potIndex === 4 ? slot.potItem4 : potIndex === 3 ? slot.potItem3 : potIndex === 2 ? slot.potItem2 : slot.potItem;
  const lampItem = potIndex === 4 ? slot.lampItem4 : potIndex === 3 ? slot.lampItem3 : potIndex === 2 ? slot.lampItem2 : slot.lampItem;

  if (!slot.tentItem || !potItem || !lampItem) return renderPotA(slot, potIndex);

  const plant = slot.activePlants.find((p) => p.potIndex === potIndex) ?? null;

  if (!plant) return renderPotB(slot, potIndex, hideout);
  if (plant.phase === 'GERMINATION')   return renderPotC(slot, potIndex, plant);
  if (plant.phase === 'GROWING')       return renderPotD(slot, potIndex, plant);
  // plant.phase ist ein handgepflegter Frontend-Typ (nicht backend-generiert) - der dritte Wert
  // wird hier bewusst weiterhin explizit geprueft statt auf `else` zu vereinfachen, als
  // Absicherung gegen Typ-Drift zwischen Backend-Response und dieser Interface-Deklaration.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- s.o.
  if (plant.phase === 'HARVEST_READY') return renderPotE(slot, potIndex, plant);
  return renderPotB(slot, potIndex, hideout);
}

function renderSlot(hideout: Hideout, slot: PropertySlot): string {
  if (!slot.isUnlocked) return '';
  const isXl     = slot.tentItem === 'growzelt_xl';
  const isGross   = slot.tentItem === 'growzelt_gross';
  const potCount  = isXl ? 4 : isGross ? 2 : 1;
  return Array.from({ length: potCount }, (_, i) => renderPot(slot, (i + 1) as 1 | 2 | 3 | 4, hideout)).join('');
}

// ─── Inventar-Tabelle ─────────────────────────────────────────────────────────

function renderInventory(h: Hideout): string {
  const harvestItems = h.inventory.filter((i) => i.itemType === 'GARDEN_OUTPUT' && i.quantity > 0);
  if (harvestItems.length === 0) return '';

  const rows = harvestItems.map((item) => {
    const parsed      = parseHempHarvestKey(item.itemKey);
    const strain      = parsed ? STRAIN_MAP.get(parsed.strainKey) : STRAIN_MAP.get(item.itemKey);
    const basePrice   = strain?.basePriceCents ?? 0;
    const modifier    = parsed ? (HEMP_QUALITY_PRICE_MODIFIERS[parsed.tier] ?? 1.00) : 1.00;
    const price       = Math.round(basePrice * modifier);
    const totalValue  = price * item.quantity;
    const strainLabel = strain?.label ?? item.itemKey;
    const badgesHtml  = renderItemBadges(item.itemKey, 'rt-badge');

    return {
      tableHtml: `<tr>
        <td class="gw-td">${esc(strainLabel)} ${badgesHtml}</td>
        <td class="gw-td">${String(item.quantity)} Stk.</td>
        <td class="gw-td" style="color:var(--accent-gold);">${esc(fmtSNox(price))}/Stk.</td>
        <td class="gw-td">${esc(fmtSNox(totalValue))}</td>
        <td class="gw-td"><button class="gw-btn gw-btn--sm gw-btn--sell" data-gw-sell="${esc(item.itemKey)}">VERKAUFEN</button></td>
      </tr>`,
      cardHeadHtml: `<span class="rt-card-name">${esc(strainLabel)}</span>${badgesHtml}`,
      cardFields: [
        { label: 'MENGE',      value: `${String(item.quantity)} Stk.` },
        { label: 'PREIS/STK.', value: `<span style="color:var(--accent-gold);">${esc(fmtSNox(price))}</span>` },
        { label: 'GESAMTWERT', value: esc(fmtSNox(totalValue)) },
      ],
      cardBottomHtml: `<div style="margin-top:var(--space-2);">
        <button class="gw-btn gw-btn--sm gw-btn--sell" data-gw-sell="${esc(item.itemKey)}">VERKAUFEN</button>
      </div>`,
    };
  });

  return `
    <div class="gw-section">
      <p class="gw-section-title">HANF-BESTAND</p>
      ${renderResponsiveTable(
        [{ header: 'SORTE' }, { header: 'MENGE' }, { header: 'PREIS/STK.' }, { header: 'GESAMTWERT' }, { header: '' }],
        rows,
      )}
      <p class="gw-note">* Richtpreise — tatsächliche Marktpreise können abweichen.</p>
    </div>`;
}

// ─── Trocknung ────────────────────────────────────────────────────────────────

function getWetTobaccoItems(h: Hideout): InventoryItem[] {
  return h.inventory.filter(
    (i) => i.itemType === 'TOBACCO_WET' && i.quantity > 0,
  );
}

function getDriedTobaccoItems(h: Hideout): InventoryItem[] {
  return h.inventory.filter(
    (i) => i.itemType === 'TOBACCO_DRIED' && i.quantity > 0,
  );
}

function renderDryingSlot(slot: DryingSlotView, propertyId: string, rackIndex: number, wetItems: InventoryItem[]): string {
  const slotLabel = `SLOT ${String(slot.slotIndex)}`;
  const key       = `${esc(propertyId)}:${String(rackIndex)}:${String(slot.slotIndex)}`;

  if (slot.occupied) {
    const label   = slot.inputItemKey ? parseTobaccoWetKey(slot.inputItemKey) : '?';
    const isDone  = slot.isDone;
    const pct     = slot.startedAt && slot.completesAt ? progressPct(slot.startedAt, slot.completesAt) : 0;
    const color   = isDone ? 'var(--accent-green)' : 'var(--accent-orange)';
    const leftStr = isDone ? 'FERTIG' : (slot.completesAt ? timeLeft(slot.completesAt) : '?');

    return `<div class="gw-slot gw-slot--${isDone ? 'harvest' : 'growing'}">
      <p class="gw-slot-title">${slotLabel} — ${esc(label)} × ${String(slot.quantity ?? 0)}</p>
      <div class="gw-prog-bar"><div class="gw-prog-fill" style="width:${String(pct)}%;background:${color};"></div></div>
      <div class="gw-slot-meta" style="color:${color};">TROCKNUNG — ${esc(leftStr)}</div>
      ${isDone && slot.jobId
        ? `<button class="gw-btn gw-btn--harvest" data-dry-collect="${esc(slot.jobId)}">ABHOLEN</button>`
        : `<button class="gw-btn" disabled>LÄUFT</button>`}
    </div>`;
  }

  // Freier Slot: Start-Formular
  if (wetItems.length === 0) {
    return `<div class="gw-slot gw-slot--unconfigured">
      <p class="gw-slot-title">${slotLabel} — FREI</p>
      <p class="gw-hint">Kein feuchter Tabak im Inventar.</p>
    </div>`;
  }

  const formKey  = dryingForm[propertyId]?.[rackIndex]?.[slot.slotIndex];
  const selKey   = formKey?.itemKey ?? '';
  const selQty   = formKey?.qty ?? 1;
  const canStart = selKey !== '';

  const opts = [
    '<option value="">— Ware wählen —</option>',
    ...wetItems.map((item) =>
      `<option value="${esc(item.itemKey)}"${selKey === item.itemKey ? ' selected' : ''}>${esc(parseTobaccoWetKey(item.itemKey))} (${String(item.quantity)} Stk.)</option>`
    ),
  ].join('');

  return `<div class="gw-slot gw-slot--ready">
    <p class="gw-slot-title">${slotLabel} — FREI</p>
    <div class="gw-slot-row">
      <select class="gw-select" data-dry-item-sel="${key}">${opts}</select>
      <select class="gw-select gw-select--qty" data-dry-qty-sel="${key}">
        ${[1,2,3,4,5].map((n) => `<option value="${String(n)}"${selQty === n ? ' selected' : ''}>${String(n)} Stk.</option>`).join('')}
      </select>
      <button class="gw-btn gw-btn--plant" data-dry-start="${key}"${!canStart ? ' disabled' : ''}>STARTEN</button>
    </div>
    <p class="gw-hint">Trocknungszeit: 3h</p>
  </div>`;
}

function renderDryingSection(h: Hideout): string {
  // NT-ORG-06 — Trocknung ist privat; Org-Gebäude produzieren nur Grow/Labor.
  if (isOrgBuilding(h)) return '';
  const status     = dryingCache.get(h.id);
  const wetItems   = getWetTobaccoItems(h);
  const driedItems = getDriedTobaccoItems(h);

  // Der linke Operand des zweiten "||" ist selbst ein bereits ausgewertetes boolean (kann
  // legitim `false` sein), kein `??`-Kandidat: "false ?? driedItems.length>0" würde immer
  // `false` liefern und die Fallthrough-Prüfung auf driedItems/racks-Bestand kaputt machen.
  const hasActivity = wetItems.length > 0
    || (status?.racks.some((r) => r.slots.some((s) => s.occupied)))
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- s.o.
    || driedItems.length > 0
    || (status && (status.installedRacks > 0 || status.rackInInventory > 0));
  if (!hasActivity) return '';

  if (!status) {
    return `<div class="gw-section">
      <p class="gw-section-title">TROCKNUNG</p>
      <p class="gw-hint">Wird geladen...</p>
    </div>`;
  }

  // Ein Panel pro installiertem Gestell
  const racksHtml = status.racks.map((rack) => {
    const hasActiveJobs = rack.slots.some((s) => s.occupied);
    const slotsHtml     = rack.slots.map((slot) => renderDryingSlot(slot, h.id, rack.rackIndex, wetItems)).join('');
    return `
      <div class="gw-equipment-row">
        <span class="gw-equip-label">TROCKNUNGSGESTELL ${String(rack.rackIndex + 1)}</span>
        <span class="gw-equip-status" style="color:var(--accent-green);">INSTALLIERT</span>
        ${!hasActiveJobs
          ? `<button class="gw-btn gw-btn--sm" data-dry-remove="${esc(h.id)}:${String(rack.rackIndex)}">ABBAUEN</button>`
          : `<button class="gw-btn gw-btn--sm" disabled title="Trocknung läuft">ABBAUEN</button>`}
      </div>
      <div class="gw-slots">${slotsHtml}</div>`;
  }).join('');

  // Weiteres Gestell installieren (wenn maxRacks > installiert und im Inventar)
  let installPanel = '';
  if (status.installedRacks === 0) {
    if (status.rackInInventory > 0) {
      installPanel = `<div class="gw-equipment-row">
        <span class="gw-equip-label">TROCKNUNGSGESTELL</span>
        <span class="gw-equip-status" style="color:var(--accent-orange);">IM LAGER</span>
        <button class="gw-btn gw-btn--sm gw-btn--install" data-dry-install="${esc(h.id)}">INSTALLIEREN</button>
      </div>`;
    } else {
      installPanel = `<div class="gw-equipment-row">
        <span class="gw-equip-label">TROCKNUNGSGESTELL</span>
        <span class="gw-equip-status" style="color:var(--text-muted);">NICHT VORHANDEN</span>
        <span class="gw-equip-hint">Im Markt kaufen</span>
      </div>`;
    }
  } else if (status.installedRacks < status.maxRacks) {
    if (status.rackInInventory > 0) {
      installPanel = `<div class="gw-equipment-row" style="margin-top:var(--space-2);">
        <span class="gw-equip-label">WEITERES GESTELL (${String(status.rackInInventory)} im Lager)</span>
        <button class="gw-btn gw-btn--sm gw-btn--install" data-dry-install="${esc(h.id)}">INSTALLIEREN</button>
      </div>`;
    } else {
      installPanel = `<p class="gw-hint" style="margin-top:var(--space-2);">Stufe ${String(h.expansionLevel)} erlaubt bis zu ${String(status.maxRacks)} Gestelle — weiteres im Markt kaufen.</p>`;
    }
  }

  // Getrockneter Tabak im Bestand
  let driedHtml = '';
  if (driedItems.length > 0) {
    const driedRows = driedItems.map((item) => {
      const sorteName  = parseTobaccoDriedSorte(item.itemKey);
      const badgesHtml = renderItemBadges(item.itemKey, 'rt-badge');
      return {
        tableHtml: `<tr>
          <td class="gw-td">${esc(sorteName)} ${badgesHtml}</td>
          <td class="gw-td">${String(item.quantity)} Stk.</td>
        </tr>`,
        cardHeadHtml: `<span class="rt-card-name">${esc(sorteName)}</span>${badgesHtml}`,
        cardFields: [{ label: 'MENGE', value: `${String(item.quantity)} Stk.` }],
      };
    });
    driedHtml = `<p class="gw-section-title" style="margin-top:var(--space-2);">GETROCKNETER TABAK</p>
      ${renderResponsiveTable([{ header: 'SORTE' }, { header: 'MENGE' }], driedRows)}`;
  }

  return `<div class="gw-section">
    <p class="gw-section-title">TROCKNUNG</p>
    ${racksHtml}
    ${installPanel}
    ${driedHtml}
  </div>`;
}

// ─── Haupt-Render ─────────────────────────────────────────────────────────────

function renderContent(): string {
  if (isLoading) {
    return '<p style="font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);padding:var(--space-4) 0;">LADE...</p>';
  }
  if (loadError) {
    return `<p style="font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--accent-red);padding:var(--space-4) 0;">FEHLER: ${esc(loadError)}</p>`;
  }
  if (growHideouts.length === 0) {
    return `<div class="gw-empty">
      <p class="gw-empty-msg">KEIN GROW ROOM VORHANDEN.</p>
      <p class="gw-empty-hint">Versteck unter IMMOBILIEN auf GROW ROOM ausbauen.</p>
    </div>`;
  }

  const h = growHideouts[selectedIdx];
  if (!h) return '';

  // Versteck-Selektor (Buttons wenn > 1 Grow Room)
  const selectorHtml = renderHideoutSelectorTabs(
    growHideouts.map((gh, i) => ({ key: String(i), label: hideoutLabel(gh), isSelected: i === selectedIdx })),
    'gw-htab',
  );

  // Upgrade-Hinweis wenn Ausbau noch läuft
  let upgradeNote = '';
  if (isUpgradeRunning(h) && h.expansionUpgradeCompletesAt !== null) {
    upgradeNote = `<p class="gw-upgrade-note">UMBAU LÄUFT — Slots noch nicht verfügbar. Fertig in: ${esc(timeLeft(h.expansionUpgradeCompletesAt))}</p>`;
  }

  // Grow-Slots
  const growSlots = h.propertySlots
    .filter((s) => s.slotType === 'GROW_TENT' && s.isUnlocked)
    .sort((a, b) => a.slotIndex - b.slotIndex);
  const slotsHtml = growSlots.length > 0
    ? `<div class="gw-slots">${growSlots.map((s) => renderSlot(h, s)).join('')}</div>`
    : `<p class="gw-hint">Keine freigeschalteten Grow-Slots vorhanden.</p>`;

  return `
    ${selectorHtml}
    <div class="gw-subheader">
      <span class="gw-subheader-name">${esc(hideoutLabel(h))}</span>
      <span class="gw-subheader-type">GROW ROOM STUFE ${String(h.expansionLevel)}</span>
    </div>
    ${upgradeNote}
    <div class="gw-section">
      <p class="gw-section-title">ANBAU</p>
      ${slotsHtml}
    </div>
    ${renderInventory(h)}
    ${renderDryingSection(h)}
  `;
}

// ─── Event-Listener ───────────────────────────────────────────────────────────

function attachListeners(): void {
  const root = document.getElementById('gw-tab-root');
  if (!root || root === listenersRoot) return;
  listenersRoot = root;

  // Sorte wählen — key = `${slotId}:${potIndex}`
  root.addEventListener('change', (e) => {
    const sel = (e.target as Element).closest<HTMLSelectElement>('[data-gw-strain-select]');
    if (!sel) return;
    const key = sel.dataset.gwStrainSelect ?? '';
    selectedStrain[key] = sel.value;
    rerender();
  });

  // Buttons (plant / water / harvest / sell)
  root.addEventListener('click', (e) => {
    const target = e.target as Element;

    const htab = target.closest<HTMLButtonElement>('[data-gw-htab]');
    if (htab) {
      const idx = parseInt(htab.dataset.gwHtab ?? '0', 10);
      if (!isNaN(idx) && idx !== selectedIdx) {
        selectedIdx = idx;
        rerender();
        const newH = growHideouts[selectedIdx];
        if (newH && !dryingCache.has(newH.id)) {
          void loadDryingStatus(newH.id).then(() => { rerender(); });
        }
      }
      return;
    }

    const plantBtn = target.closest<HTMLButtonElement>('[data-gw-plant]');
    if (plantBtn && !plantBtn.disabled) {
      const key = plantBtn.dataset.gwPlant ?? '';          // format: `${slotId}:${potIndex}`
      const [slotId, potIndexStr] = key.split(':');
      const potIndex = parseInt(potIndexStr ?? '1', 10) as 1 | 2 | 3 | 4;
      const strain   = selectedStrain[key] ?? '';
      if (strain && slotId) {
        plantBtn.disabled = true;
        runExclusive(`plant:${key}`, () => handlePlant(slotId, strain, potIndex));
      }
      return;
    }

    const waterBtn = target.closest<HTMLButtonElement>('[data-gw-water]');
    if (waterBtn && !waterBtn.disabled) {
      const plantId = waterBtn.dataset.gwWater ?? '';
      waterBtn.disabled = true;
      runExclusive(`water:${plantId}`, () => handleWater(plantId));
      return;
    }

    const harvestBtn = target.closest<HTMLButtonElement>('[data-gw-harvest]');
    if (harvestBtn && !harvestBtn.disabled) {
      const plantId = harvestBtn.dataset.gwHarvest ?? '';
      harvestBtn.disabled = true;
      runExclusive(`harvest:${plantId}`, () => handleHarvest(plantId));
      return;
    }

    const applyBtn = target.closest<HTMLButtonElement>('[data-gw-apply]');
    if (applyBtn && !applyBtn.disabled) {
      const plantId = applyBtn.dataset.gwApply ?? '';
      const itemKey = applyBtn.dataset.gwApplyItem ?? '';
      applyBtn.disabled = true;
      runExclusive(`apply:${plantId}`, () => handleApply(plantId, itemKey));
      return;
    }

    const sellBtn = target.closest<HTMLButtonElement>('[data-gw-sell]');
    if (sellBtn) {
      // Zur Schwarzmarkt-Sektion in Crime Ops navigieren
      sessionStorage.setItem('crimeops_tab', 'schwarzmarkt');
      sessionStorage.setItem('crimeops_sell_strain', sellBtn.dataset.gwSell ?? '');
      document.querySelector<HTMLButtonElement>('[data-co-section="schwarzmarkt"]')?.click();
      return;
    }

    // Trocknung starten
    const dryStartBtn = target.closest<HTMLButtonElement>('[data-dry-start]');
    if (dryStartBtn && !dryStartBtn.disabled) {
      const parts = (dryStartBtn.dataset.dryStart ?? '').split(':');
      const propertyId = parts[0];
      const rackIndex  = parseInt(parts[1] ?? '0', 10);
      const slotIndex  = parseInt(parts[2] ?? '0', 10);
      if (propertyId && !isNaN(rackIndex) && !isNaN(slotIndex)) {
        const form = dryingForm[propertyId]?.[rackIndex]?.[slotIndex];
        if (form?.itemKey) {
          dryStartBtn.disabled = true;
          void handleStartDrying(propertyId, rackIndex, slotIndex, form.itemKey, form.qty);
        }
      }
      return;
    }

    // Getrocknete Ware abholen
    const dryCollectBtn = target.closest<HTMLButtonElement>('[data-dry-collect]');
    if (dryCollectBtn && !dryCollectBtn.disabled) {
      const jobId = dryCollectBtn.dataset.dryCollect ?? '';
      if (jobId) {
        dryCollectBtn.disabled = true;
        void handleCollectDrying(jobId);
      }
      return;
    }

    // Trocknungsgestell installieren
    const dryInstallBtn = target.closest<HTMLButtonElement>('[data-dry-install]');
    if (dryInstallBtn && !dryInstallBtn.disabled) {
      const propertyId = dryInstallBtn.dataset.dryInstall ?? '';
      if (propertyId) {
        dryInstallBtn.disabled = true;
        void handleAssignDryingRack(propertyId);
      }
      return;
    }

    // Trocknungsgestell abbauen
    const dryRemoveBtn = target.closest<HTMLButtonElement>('[data-dry-remove]');
    if (dryRemoveBtn && !dryRemoveBtn.disabled) {
      const parts      = (dryRemoveBtn.dataset.dryRemove ?? '').split(':');
      const propertyId = parts[0];
      const rackIndex  = parseInt(parts[1] ?? '0', 10);
      if (propertyId && !isNaN(rackIndex)) {
        dryRemoveBtn.disabled = true;
        void handleRemoveDryingRack(propertyId, rackIndex);
      }
      return;
    }
  });

  // Trocknungs-Formular: Item wählen
  root.addEventListener('change', (e) => {
    const itemSel = (e.target as Element).closest<HTMLSelectElement>('[data-dry-item-sel]');
    if (itemSel) {
      const parts      = (itemSel.dataset.dryItemSel ?? '').split(':');
      const propertyId = parts[0];
      const rackIndex  = parseInt(parts[1] ?? '0', 10);
      const slotIndex  = parseInt(parts[2] ?? '0', 10);
      if (propertyId && !isNaN(rackIndex) && !isNaN(slotIndex)) {
        const propMap  = (dryingForm[propertyId] ??= {});
        const rackMap  = (propMap[rackIndex] ??= {});
        const existing = rackMap[slotIndex];
        rackMap[slotIndex] = { itemKey: itemSel.value, qty: existing?.qty ?? 1 };
        rerender();
      }
      return;
    }

    const qtySel = (e.target as Element).closest<HTMLSelectElement>('[data-dry-qty-sel]');
    if (qtySel) {
      const parts      = (qtySel.dataset.dryQtySel ?? '').split(':');
      const propertyId = parts[0];
      const rackIndex  = parseInt(parts[1] ?? '0', 10);
      const slotIndex  = parseInt(parts[2] ?? '0', 10);
      if (propertyId && !isNaN(rackIndex) && !isNaN(slotIndex)) {
        const propMap  = (dryingForm[propertyId] ??= {});
        const rackMap  = (propMap[rackIndex] ??= {});
        const existing = rackMap[slotIndex];
        rackMap[slotIndex] = { itemKey: existing?.itemKey ?? '', qty: parseInt(qtySel.value, 10) };
      }
      return;
    }
  });
}

// ─── API-Aktionen ─────────────────────────────────────────────────────────────

async function handlePlant(slotId: string, strain: string, potIndex: 1 | 2 | 3 | 4 = 1): Promise<void> {
  try {
    await api.post<unknown>(`/grow/slots/${encodeURIComponent(slotId)}/plant`, { strain, potIndex });
    Reflect.deleteProperty(selectedStrain, potKey(slotId, potIndex));
    await reload();
    showToast('Samen gepflanzt.', 'info');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Pflanzen fehlgeschlagen.', 'error');
    rerender();
  }
}

async function handleWater(plantId: string): Promise<void> {
  try {
    await api.post<unknown>(`/grow/plants/${encodeURIComponent(plantId)}/water`, {});
    cooldownOverrideUntilMs.delete(plantId);
    await reload();
    showToast('Gegossen.', 'info');
  } catch (err) {
    if (err instanceof ApiError && err.code === 'WATER_COOLDOWN') {
      const remainingMsRaw = err.details.remainingMs;
      if (typeof remainingMsRaw === 'number' && remainingMsRaw > 0) {
        cooldownOverrideUntilMs.set(plantId, Date.now() + remainingMsRaw);
      }
    }
    showToast(err instanceof ApiError ? err.message : 'Gießen fehlgeschlagen.', 'error');
    rerender();
  }
}

async function handleApply(plantId: string, itemKey: string): Promise<void> {
  try {
    const res = await api.post<{ message: string }>(`/grow/plants/${encodeURIComponent(plantId)}/apply`, { itemKey });
    await reload();
    showToast(res.message, 'info');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Anwenden fehlgeschlagen.', 'error');
    rerender();
  }
}

async function handleHarvest(plantId: string): Promise<void> {
  try {
    const res = await api.post<{ quantity: number; unit: string }>(`/grow/plants/${encodeURIComponent(plantId)}/harvest`, {});
    await reload();
    showToast(`Ernte: ${String(res.quantity)} Stk.`, 'info');
  } catch (err) {
    if (tryOfferEmergencyGarage(err, () => { void reload(); })) { rerender(); return; }
    showToast(err instanceof ApiError ? err.message : 'Ernte fehlgeschlagen.', 'error');
    rerender();
  }
}

async function handleStartDrying(propertyId: string, rackIndex: number, slotIndex: number, inputItemKey: string, quantity: number): Promise<void> {
  try {
    await api.post<unknown>(`/drying/${encodeURIComponent(propertyId)}/racks/${String(rackIndex)}/slots/${String(slotIndex)}/start`, { inputItemKey, quantity });
    // Formular-Slot zurücksetzen
    const propMap = dryingForm[propertyId];
    if (propMap?.[rackIndex]) {
      const newRackMap = { ...propMap[rackIndex] };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete newRackMap[slotIndex];
      propMap[rackIndex] = newRackMap;
    }
    await loadDryingStatus(propertyId);
    await reload();
    showToast('Trocknung gestartet.', 'info');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Trocknung fehlgeschlagen.', 'error');
    rerender();
  }
}

async function handleAssignDryingRack(propertyId: string): Promise<void> {
  try {
    await api.post<unknown>(`/drying/${encodeURIComponent(propertyId)}/rack`, {});
    await loadDryingStatus(propertyId);
    rerender();
    showToast('Trocknungsgestell installiert.', 'info');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Installation fehlgeschlagen.', 'error');
    rerender();
  }
}

async function handleRemoveDryingRack(propertyId: string, rackIndex: number): Promise<void> {
  try {
    await api.delete<unknown>(`/drying/${encodeURIComponent(propertyId)}/rack/${String(rackIndex)}`);
    await loadDryingStatus(propertyId);
    await reload();
    showToast('Trocknungsgestell abgebaut.', 'info');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Abbauen fehlgeschlagen.', 'error');
    rerender();
  }
}

async function handleCollectDrying(jobId: string): Promise<void> {
  try {
    const res = await api.post<{ outputItemKey: string; quantity: number }>(`/drying/jobs/${encodeURIComponent(jobId)}/collect`, {});
    const h = growHideouts[selectedIdx];
    if (h) await loadDryingStatus(h.id);
    await reload();
    showToast(`Abgeholt: ${parseTobaccoDriedKey(res.outputItemKey)} × ${String(res.quantity)}`, 'info');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Abholen fehlgeschlagen.', 'error');
    rerender();
  }
}

function showToast(msg: string, type: 'info' | 'error'): void {
  const color = type === 'error' ? 'var(--accent-red)' : 'var(--accent-green)';
  const t = document.createElement('div');
  t.style.cssText = `position:fixed;bottom:80px;left:50%;transform:translateX(-50%);
    background:var(--bg-elevated);border:1px solid ${color};font-family:var(--font-mono);
    font-size:0.95rem;color:${color};padding:8px 20px;z-index:500;border-radius:2px;
    letter-spacing:.05em;white-space:nowrap;`;
  t.textContent = msg;
  t.addEventListener('click', () => { t.remove(); });
  document.body.appendChild(t);
  setTimeout(() => { t.remove(); }, calcNoticeDisplayMs(msg, type === 'error'));
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function addStyles(): void {
  injectResponsiveTableStyles();
  if (document.getElementById('gw-tab-styles')) return;
  const s = document.createElement('style');
  s.id = 'gw-tab-styles';
  s.textContent = `
    .gw-subheader{display:flex;align-items:baseline;gap:var(--space-3);
      margin-bottom:var(--space-3);padding-bottom:var(--space-2);
      border-bottom:1px solid var(--border);}
    .gw-subheader-name{font-family:var(--font-mono);font-size:var(--font-size-sm);letter-spacing:.06em;
      color:var(--text-primary);text-transform:uppercase;}
    .gw-subheader-type{font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.06em;
      color:var(--accent-green);text-transform:uppercase;}

    .gw-upgrade-note{font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--accent-orange);
      border:1px solid var(--accent-orange);padding:6px 12px;border-radius:2px;
      margin-bottom:var(--space-2);}

    .gw-section{border:1px solid var(--border);border-radius:var(--radius);
      padding:var(--space-3);margin-bottom:var(--space-3);background:var(--bg-surface);}
    .gw-section-title{font-family:var(--font-mono);font-size:0.875rem;letter-spacing:.1em;
      color:var(--text-muted);text-transform:uppercase;margin:0 0 var(--space-2);}

    .gw-slots{display:flex;flex-direction:column;gap:var(--space-2);}

    .gw-slot{border:1px solid var(--border);border-radius:var(--radius);
      padding:var(--space-2) var(--space-3);background:var(--bg-elevated);}
    .gw-slot--unconfigured{border-color:color-mix(in srgb, var(--border) 60%, transparent);
      opacity:.7;}
    .gw-slot--ready{border-color:var(--accent-blue);}
    .gw-slot--growing{border-color:var(--accent-green);}
    .gw-slot--harvest{border-color:var(--accent-green);}

    .gw-slot-title{font-family:var(--font-mono);font-size:var(--font-size-sm);letter-spacing:.07em;
      text-transform:uppercase;color:var(--text-primary);margin:0 0 var(--space-2);}

    .gw-slot-row{display:flex;gap:var(--space-2);align-items:center;margin-bottom:var(--space-1);}

    .gw-strain-info{display:flex;flex-direction:column;gap:2px;
      margin:var(--space-2) 0;padding:var(--space-2);
      background:var(--bg-surface);border:1px solid var(--border);border-radius:2px;}
    .gw-si-row{display:flex;gap:var(--space-2);align-items:baseline;}
    .gw-si-key{font-family:var(--font-mono);font-size:0.875rem;letter-spacing:.06em;
      color:var(--text-muted);text-transform:uppercase;min-width:90px;}
    .gw-si-val{font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--text-primary);}

    .gw-prog-bar{height:6px;background:var(--bg-surface);border:1px solid var(--border);
      border-radius:2px;overflow:hidden;margin-bottom:var(--space-1);}
    .gw-prog-fill{height:100%;transition:width 1s;}

    .gw-slot-meta{font-family:var(--font-mono);font-size:var(--font-size-sm);margin-bottom:var(--space-2);}

    .gw-hint{font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-italic);margin:0;}
    .gw-hint-green{font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--accent-green);
      margin:0 0 var(--space-2);}
    .gw-note{font-family:var(--font-mono);font-size:0.875rem;color:var(--text-muted);
      margin:var(--space-1) 0 0;}

    .gw-select{font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.04em;
      background:var(--bg-surface);border:1px solid var(--border);border-radius:2px;
      color:var(--text-primary);padding:4px 6px;cursor:pointer;flex:1;}
    .gw-select--qty{flex:0 0 60px;}

    .gw-btn{font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.07em;
      text-transform:uppercase;padding:6px 14px;background:transparent;
      border-radius:var(--radius-btn);cursor:pointer;border:1px solid var(--border);
      color:var(--text-muted);white-space:nowrap;}
    .gw-btn:disabled{opacity:.38;cursor:not-allowed;}
    .gw-btn--plant {border-color:var(--accent-blue);  color:var(--accent-blue);}
    .gw-btn--water {border-color:var(--accent-blue);  color:var(--accent-blue);}
    .gw-btn--harvest{border-color:var(--accent-green); color:var(--accent-green);}
    .gw-btn--sell  {border-color:var(--accent-gold);  color:var(--accent-gold);}
    .gw-btn--apply {border-color:var(--accent-purple);color:var(--accent-purple);}
    .gw-apply-row  {display:flex;flex-wrap:wrap;gap:var(--space-2);margin-top:var(--space-1);}
    .gw-btn--sm    {padding:3px 8px;font-size:0.875rem;}
    .gw-water-save {color:var(--accent-green);font-size:0.875rem;}
    .gw-water-status{font-family:var(--font-mono);font-size:var(--font-size-xs);
      letter-spacing:.06em;color:var(--text-muted);text-transform:uppercase;
      margin:var(--space-1) 0 0;}
    .gw-water-status-ready{color:var(--accent-green);}
    .gw-water-status-time{color:var(--text-muted);}

    .gw-table{width:100%;border-collapse:collapse;font-family:var(--font-mono);font-size:var(--font-size-sm);}
    .gw-th{padding:4px 8px;text-align:left;font-size:0.875rem;letter-spacing:.08em;
      color:var(--text-muted);border-bottom:1px solid var(--border);text-transform:uppercase;}
    .gw-td{padding:5px 8px;
      border-bottom:1px solid color-mix(in srgb, var(--border) 50%, transparent);
      color:var(--text-primary);}

    .gw-empty{padding:var(--space-4) 0;}
    .gw-empty-msg{font-family:var(--font-mono);font-size:var(--font-size-sm);letter-spacing:.06em;
      color:var(--text-muted);text-transform:uppercase;margin:0 0 var(--space-1);}
    .gw-empty-hint{font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-italic);margin:0;}
  `;
  document.head.appendChild(s);
}

// ─── Daten laden ──────────────────────────────────────────────────────────────

async function loadDryingStatus(propertyId: string): Promise<void> {
  const target = growHideouts.find((gh) => gh.id === propertyId);
  if (target && isOrgBuilding(target)) return;
  try {
    const res = await api.get<DryingStatusView>(`/drying/${encodeURIComponent(propertyId)}/status`);
    dryingCache.set(propertyId, res);
  } catch {
    // Fehler ignorieren — Sektion bleibt im Lade-Zustand
  }
}

/** @param background true = Aufruf aus einem Timer (Render-Pause bei offener Auswahl, H7). */
async function reload(background = false): Promise<void> {
  if (reloadPromise) return reloadPromise;

  reloadPromise = (async () => {
    try {
      const res = await api.get<{ hideouts: Hideout[] }>('/hideouts?includeOrg=1');
      // Nur Verstecke mit aktivem Grow Room (nicht mitten im Umbau)
      growHideouts = filterHideoutsByExpansion(res.hideouts, 'GROW_ROOM');
      if (selectedIdx >= growHideouts.length) selectedIdx = 0;
      loadError = null;

      // Trocknungs-Status für ausgewähltes Versteck nachladen
      const h = growHideouts[selectedIdx];
      if (h) await loadDryingStatus(h.id);
    } catch (err) {
      loadError = err instanceof ApiError ? err.message : 'Laden fehlgeschlagen.';
    }
    isLoading = false;
    if (background) renderWhenIdle(GW_RENDER_KEY, 'gw-tab-root', rerender);
    else rerender();
  })();

  try {
    await reloadPromise;
  } finally {
    reloadPromise = null;
  }
}

export async function refreshGewaechshausTab(): Promise<void> {
  await reload();
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

/** Initiales HTML-Skelett */
export function renderGewaechshausTab(): string {
  return `<div id="gw-tab-root" style="padding:var(--space-2) 0;">
    <p style="font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);padding:var(--space-4) 0;">LADE...</p>
  </div>`;
}

/** Aufgerufen wenn crimeOps die Sektion 'gewaechshaus' aktiviert */
export function mountGewaechshausTab(): void {
  addStyles();
  if (refreshTimer !== null) { clearInterval(refreshTimer); refreshTimer = null; }
  if (countdownTimer !== null) { clearInterval(countdownTimer); countdownTimer = null; }
  isLoading = growHideouts.length === 0;
  loadError = null;
  if (!isLoading) rerender();
  void reload();
  refreshTimer = setInterval(() => { void reload(true); }, 30_000);
  countdownTimer = setInterval(() => {
    const root = document.getElementById('gw-tab-root');
    if (!root) return;
    // NT-HOTFIX-01 (H7): Countdown-Render nie über ein offenes Dropdown/Modal hinweg.
    if (isUserInteracting(root)) return;

    if (hasExpiredPhase()) { void reload(true); return; }
    if (!hasActiveWaterCooldowns()) return;
    rerender();
  }, 1_000);
}

/** Aufgerufen wenn crimeOps die Sektion verlässt */
export function cleanupGewaechshausTab(): void {
  if (refreshTimer !== null) { clearInterval(refreshTimer); refreshTimer = null; }
  if (countdownTimer !== null) { clearInterval(countdownTimer); countdownTimer = null; }
  growHideouts   = [];
  selectedIdx    = 0;
  selectedStrain = {};
  cooldownOverrideUntilMs.clear();
  dryingCache.clear();
  dryingForm     = {};
  isLoading      = true;
  loadError      = null;
  listenersRoot  = null;
  inFlight.clear();
  cancelPendingRender(GW_RENDER_KEY);
}

